import {
  ButtonColor,
  ButtonShape,
  ButtonSize,
  Divider,
  DropdownButton,
  DropdownItem,
  Icon,
  IconName,
  IconSize,
  IconVariant,
  useModalConfirmation,
  useToastManager
} from "@quortex/front-common"
import { useNavigate } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { omit } from "lodash-es"
import type { FC } from "react"
import { useToggleOttEndpoint } from "@/App/play/components/features/endpoints/ottEndpoints/hooks"
import PlayPathComposition from "@/App/play/components/features/endpoints/ottEndpoints/PlayPathComposition"
import type { ShareUrlModalGenerics } from "@/App/play/components/wired/modals/ShareUrlModal"
import { usePoolPath } from "@/App/play/hooks/navigation"
import { useDeleteOttEndpoint } from "@/queries/playOttEndpoints"
import { usePool } from "@/queries/playPools"

type Props = {
  endpoint: api.ott.OttEndpoint
}

const OttEndpointActionMenu: FC<Props> = ({ endpoint }) => {
  const navigate = useNavigate()
  const poolPath = usePoolPath()
  const { data: pool } = useSuspenseQuery(usePool())
  const { openConfirmationModal } = useModalConfirmation()
  const { mutateAsync: deleteOttEndpoint } = useMutation(
    useDeleteOttEndpoint({ ottEndpointUuid: endpoint.uuid })
  )

  const toastManager = useToastManager()

  const handleDeleteOttEndpoint = async () => {
    try {
      await deleteOttEndpoint()

      toastManager.success("Endpoint deleted")
      navigate({
        replace: true,
        search: old => ({ ...omit(old, "modal") }),
        to: `${poolPath}/live/endpoints/ottendpoints`
      })
    } catch (e) {
      toastManager.error({ apiError: e })
      throw e
    }
  }

  const { toggleOttEndpointState } = useToggleOttEndpoint(endpoint.uuid)

  return (
    <DropdownButton
      body={
        <>
          <DropdownItem
            data-testid="share-url-btn"
            to={{
              search: old =>
                ({
                  ...old,
                  modal: { name: "share_url", ottEndpointUuid: endpoint.uuid }
                }) satisfies ShareUrlModalGenerics["Search"]
            }}
            type="link"
            onClick={e => e.stopPropagation()}
          >
            <Icon
              aria-hidden
              icon={IconName.SHARE}
              size={IconSize.SMALL}
              type={IconVariant.SOLID}
            />
            Share URL
          </DropdownItem>

          <DropdownItem
            data-testid="disable-btn"
            disabled={endpoint.status === "configuring"}
            type="button"
            onClick={() => toggleOttEndpointState(!endpoint.enabled)}
          >
            <Icon
              aria-hidden
              icon={endpoint.enabled ? IconName.FORBIDDEN : IconName.CHECK}
              size={IconSize.SMALL}
              type={IconVariant.SOLID}
            />
            {endpoint.enabled ? "Disable" : "Enable"}
          </DropdownItem>

          <Divider />

          <DropdownItem
            className="text-red-600"
            data-testid="delete-btn"
            type="button"
            onClick={e => {
              openConfirmationModal({
                children: (
                  <ul className="mx-md list-disc">
                    <li>
                      The following path will be deleted:{" "}
                      <PlayPathComposition
                        className="mt-2xs"
                        endpoint={endpoint}
                        processingType={pool.type}
                      />
                    </li>
                    <li className="mt-sm">
                      The corresponding input, processing and packaging will not be removed.{" "}
                    </li>
                  </ul>
                ),
                onAccept: handleDeleteOttEndpoint,
                title: "Delete endpoint?",
                variant: "destructive"
              })
              e.stopPropagation()
            }}
          >
            <Icon
              aria-hidden
              icon={IconName.TRASH}
              size={IconSize.SMALL}
              type={IconVariant.SOLID}
            />
            Delete
          </DropdownItem>
        </>
      }
      color={ButtonColor.SECONDARY}
      data-testid="more-option-btn"
      hideCaret
      shape={ButtonShape.ICON}
      size={ButtonSize.SMALL}
      type="button"
    >
      <Icon icon={IconName.ELLIPSIS} size={IconSize.SMALL} type={IconVariant.SOLID} />
    </DropdownButton>
  )
}

export default OttEndpointActionMenu
